import React, { Children, isValidElement, useState } from "react";
import { Link } from "react-router-dom";
import { Icon, Menu, Sidebar } from "semantic-ui-react";

import { Can, checkPermissions } from "components";
import "components/MenuView/MenuView.scss";
import { useUserRoleACLs } from "hooks";

import type { ReactNode } from "react";
import type { PATHS } from "router/paths";

type MenuViewProps = {
  isSideBarOpen: boolean;
  children: React.ReactNode;
  hideSidebar: () => void;
};

type MenuViewSectionProps = {
  children: ReactNode;
  title?: string;
  isExpandedByDefault?: boolean;
};

type MenuItemProps = {
  title: string;
  link: PATHS;
  resource: string | string[];
  activity: string | string[];
  icon?: string;
  condition?: boolean;
  textIcon?: string;
};

const TextIcon = ({ textIcon }: { textIcon: string }) => <span className="text-icon">{textIcon}</span>;

export const MenuItem = ({ title, icon, link, resource, activity, condition, textIcon }: MenuItemProps) => {
  if (condition === false) return;

  return (
    <Can I={activity} the={resource} key={link}>
      <Link to={link}>
        <Menu.Item className="menu-view-item">
          {textIcon ? <TextIcon textIcon={textIcon} /> : <Icon className={icon} />}
          {title}
        </Menu.Item>
      </Link>
    </Can>
  );
};

export const MenuView = ({ children, isSideBarOpen, hideSidebar }: MenuViewProps) => {
  const handleSidebarClick = (e: React.MouseEvent) => {
    if (e.target instanceof Element && e.target.closest("a") && !e.target.closest("a")?.classList.contains("heading")) hideSidebar();
  };

  return (
    <Sidebar
      as={Menu}
      animation="overlay"
      icon="labeled"
      inverted
      vertical
      visible={isSideBarOpen}
      onHide={hideSidebar}
      onClick={handleSidebarClick}
      width="thin"
      className="MenuView"
    >
      {children}
    </Sidebar>
  );
};

export const MenuSection = ({ title, children, isExpandedByDefault = true }: MenuViewSectionProps) => {
  const [showItems, setShowItems] = useState(isExpandedByDefault);
  const handleChangeShowItems = () => setShowItems(showItems => !showItems);
  const roleACLs = useUserRoleACLs();

  const visibleItems = Children.toArray(children).filter(child => {
    if (!isValidElement<MenuItemProps>(child)) return false;
    const { condition, resource, activity } = child.props;
    if (condition === false) return false;
    return checkPermissions(roleACLs, resource, activity);
  });

  if (visibleItems.length === 0) return;

  return (
    <>
      {title && (
        <Menu.Item onClick={handleChangeShowItems} className="menu-view-item heading">
          {title}
          <Icon name={`chevron ${showItems ? "right" : "down"}`} />
        </Menu.Item>
      )}
      {showItems && visibleItems}
    </>
  );
};
